import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, BellRing, X } from "lucide-react";
import AktivitaDetail from "./AktivitaDetail";
import NovaAktivita from "./NovaAktivita";
import { useObnovaPriNavratu } from "../lib/obnova";
import { obnovitOdber, stavPush, zapnoutPush, zaregistrovat } from "../lib/push";
import { UpozorneniCtx, useUpozorneni } from "../lib/upozorneniKontext";
import api from "../lib/api";

/**
 * Upozornění ve zvonečku pro celou aplikaci: načtou se každých 30 s (jen když
 * je aplikace vidět) a po návratu do ní. Odtud se otevírá i úkol — ze zvonečku,
 * z upozornění na zařízení i z odkazu v e-mailu (`/?aktivita=ID`), a otevřením
 * jeho upozornění zhasnou. Bez upozornění na zařízení blikne okénko v aplikaci.
 */
export { useUpozorneni };
const OKENKO = ["AKTUALNI", "PRIPOMINKA", "ZMINKA"];   // tahle upozornění bliknou okénkem

const shoda = (kde) => (u) => !u.precteno && (
  kde.id ? u.id === kde.id : kde.aktivita_id ? u.aktivita_id === kde.aktivita_id : true);

export function UpozorneniProvider({ children }) {
  const [stav, setStav] = useState({ items: [], neprectenych: 0, nacteno: false });
  const [aktivitaId, setAktivitaId] = useState(null);
  const [upravit, setUpravit] = useState(null);
  const [push, setPush] = useState("nejde");
  const [okenka, setOkenka] = useState([]);
  const videno = useRef(null);       // id upozornění, která už byla vidět (null = první načtení)
  const pushRef = useRef(push);
  pushRef.current = push;
  const loc = useLocation();
  const nav = useNavigate();

  const obnovit = useCallback(() => {
    api.get("/api/upozorneni")
      .then(({ data }) => {
        const items = data.items || [];
        // Nové „teď“ a připomínky bliknou okénkem — jen když nechodí upozornění na zařízení.
        if (videno.current && pushRef.current !== "zapnuto") {
          const nova = items.filter((u) => !u.precteno && OKENKO.includes(u.typ) && !videno.current.has(u.id));
          if (nova.length) setOkenka((o) => [...nova, ...o].slice(0, 3));
        }
        videno.current = new Set(items.map((u) => u.id));
        setStav({ items, neprectenych: data.neprectenych || 0, nacteno: true });
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    obnovit();
    const t = setInterval(() => { if (document.visibilityState === "visible") obnovit(); }, 30000);
    return () => clearInterval(t);
  }, [obnovit]);
  useObnovaPriNavratu(obnovit);

  useEffect(() => {
    zaregistrovat().then(() => stavPush()).then(setPush).catch(() => {});
    obnovitOdber();
  }, []);
  /** Vrací nový stav; „chyba“, když prohlížeč odběr odmítl (třeba anonymní okno). */
  const zapnout = useCallback(async () => {
    try {
      const v = await zapnoutPush();
      setPush(v);
      return v;
    } catch {
      setPush(await stavPush().catch(() => "nejde"));
      return "chyba";
    }
  }, []);

  /** {id} jedno, {aktivita_id} vše k úkolu, {} vše. Zhasne hned, server dožene. */
  const precist = useCallback((kde = {}) => {
    setStav((s) => {
      const je = shoda(kde);
      const n = s.items.filter(je).length;
      return {
        ...s, items: s.items.map((u) => (je(u) ? { ...u, precteno: true } : u)),
        neprectenych: kde.id || kde.aktivita_id ? Math.max(0, s.neprectenych - n) : 0,
      };
    });
    api.post("/api/upozorneni/precteno", kde).catch(() => {});
  }, []);

  const otevrit = useCallback((u) => {
    precist(u.aktivita_id ? { aktivita_id: u.aktivita_id } : { id: u.id });
    if (u.aktivita_id) setAktivitaId(u.aktivita_id);
    else if (u.odkaz) nav(u.odkaz);
  }, [precist, nav]);

  // Klik na upozornění na zařízení, když je aplikace otevřená (posílá sw.js).
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;
    const zprava = (e) => {
      if (e.data?.typ !== "otevrit") return;
      const u = new URL(e.data.url);
      const id = u.searchParams.get("aktivita");
      if (id) { setAktivitaId(id); precist({ aktivita_id: id }); }   // úkol se otevře tam, kde člověk je
      else nav(`${u.pathname}${u.search}`);
    };
    navigator.serviceWorker.addEventListener("message", zprava);
    return () => navigator.serviceWorker.removeEventListener("message", zprava);
  }, [nav, precist]);

  // Odkaz z e-mailu: /?aktivita=ID otevře úkol a parametr z adresy zmizí.
  useEffect(() => {
    const p = new URLSearchParams(loc.search);
    const id = p.get("aktivita");
    if (!id) return;
    p.delete("aktivita");
    nav({ pathname: loc.pathname, search: p.toString() ? `?${p}` : "" }, { replace: true });
    setAktivitaId(id);
    precist({ aktivita_id: id });
  }, [loc.search, loc.pathname, nav, precist]);

  const zavritOkenko = (id) => setOkenka((o) => o.filter((u) => u.id !== id));
  return (
    <UpozorneniCtx.Provider value={{ ...stav, push, obnovit, precist, otevrit, zapnout }}>
      {children}
      {okenka.length > 0 && (
        <div className="fixed z-[60] inset-x-3 top-[calc(env(safe-area-inset-top)+64px)] lg:inset-x-auto lg:top-auto lg:right-5 lg:bottom-5 lg:w-[360px] flex flex-col gap-2"
          role="status" aria-live="polite" data-testid="okenka">
          {okenka.map((u) => (
            <div key={u.id} className="okno-in bg-white rounded-[16px] shadow-[0_18px_50px_rgba(20,22,26,.22)] border border-line-softer p-3 flex items-start gap-3"
              data-testid={`okenko-${u.id}`}>
              <span className="h-9 w-9 rounded-[11px] bg-accent/15 text-[#a8410c] inline-flex items-center justify-center shrink-0">
                <BellRing size={17} strokeWidth={2} />
              </span>
              <button type="button" onClick={() => { zavritOkenko(u.id); otevrit(u); }} className="flex-1 min-w-0 text-left">
                <span className="block t-telo font-700 text-ink line-clamp-2">{u.titulek}</span>
                {u.text && <span className="block t-meta text-muted truncate">{u.text}</span>}
                <span className="block t-meta font-700 text-accent mt-0.5">Otevřít</span>
              </button>
              <button type="button" onClick={() => zavritOkenko(u.id)} aria-label="Zavřít"
                className="tap lg:min-h-0 lg:min-w-0 -mr-1 -mt-1 inline-flex items-center justify-center text-muted hover:text-ink">
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
      {aktivitaId && (
        <AktivitaDetail id={aktivitaId} onClose={() => setAktivitaId(null)}
          onEdit={(a) => { setAktivitaId(null); setUpravit(a); }} onChanged={obnovit} />
      )}
      {upravit && (
        <NovaAktivita aktivita={upravit} onClose={() => setUpravit(null)} onSaved={() => { setUpravit(null); obnovit(); }} />
      )}
    </UpozorneniCtx.Provider>
  );
}

/** Číslo nepřečtených na zvonku (od 10 „9+“). */
export function PocetNaZvonku({ n, ring }) {
  if (!n) return null;
  return (
    <span data-testid="zvonek-pocet"
      className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10.5px] font-800 leading-none amount inline-flex items-center justify-center ring-2 ${ring}`}>
      {n > 9 ? "9+" : n}
    </span>
  );
}

/** Zvonek v levém panelu na počítači. */
export function ZvonekPanel({ onClick, otevreno, collapsed }) {
  const { neprectenych } = useUpozorneni();
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onClick(); }} data-testid="sidebar-zvonek"
      aria-label={neprectenych ? `Upozornění: ${neprectenych} nepřečtených` : "Upozornění"} aria-expanded={otevreno}
      title={collapsed ? "Upozornění" : undefined}
      className={`relative h-9 w-9 shrink-0 rounded-icon inline-flex items-center justify-center transition-colors ${
        otevreno ? "bg-sidebar-3 text-ondark" : "text-ondark-muted hover:text-ondark hover:bg-sidebar-1"}`}>
      <Bell size={17} strokeWidth={1.8} />
      <PocetNaZvonku n={neprectenych} ring="ring-sidebar" />
    </button>
  );
}
